import { useEffect } from "react";
import { useNotification } from "../config/Notification";

/**
 * Hook para capturar erros globais não tratados e exibi-los de forma amigável
 *
 * COMPORTAMENTO INTELIGENTE:
 * - EM DESENVOLVIMENTO: Só intercepta erros de rede/API, deixa erros do React passarem
 * - EM PRODUÇÃO: Intercepta mais erros, mas preserva erros críticos do React
 *
 * ERROS QUE SÃO INTERCEPTADOS:
 * - Erros de rede (Network Error, timeout)
 * - Erros de API (401, 404, 500, Axios errors)
 *
 * ERROS QUE NÃO SÃO INTERCEPTADOS (preservados para debugging):
 * - Erros de componentes React
 * - Erros de sintaxe/desenvolvimento
 * - Warnings do React
 * - Erros de compilação
 */
export const useGlobalErrorHandler = () => {
  const { addNotification } = useNotification();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      const errorMessage = error?.message || "";
      const errorStack = error?.stack || "";

      const isNetworkError =
        errorMessage.includes("Network Error") ||
        errorMessage.includes("timeout") ||
        errorMessage.includes("ERR_NETWORK");

      const isApiError =
        errorMessage.includes("401") ||
        errorMessage.includes("404") ||
        errorMessage.includes("500") ||
        errorMessage.includes("AxiosError");

      const isReactDevelopmentError =
        errorStack.includes("react-dom") ||
        errorStack.includes("react-refresh") ||
        errorMessage.includes("Warning:") ||
        errorMessage.includes("Failed to compile") ||
        errorMessage.includes("Uncaught Error:") ||
        errorMessage.includes("Cannot read prop");

      if (process.env.NODE_ENV === "development") {
        // Só interceptar erros de rede/API em desenvolvimento
        if (isNetworkError || isApiError) {
          console.error(
            "Erro de rede/API interceptado em desenvolvimento:",
            error
          );
          addNotification({
            type: "error",
            content: "Problema de conexão com o servidor.",
          });
          event.preventDefault();
        }
        return;
      }

      if (isNetworkError || isApiError) {
        console.error("Erro de rede/API interceptado:", error);
        addNotification({
          type: "error",
          content: "Ocorreu um problema de conexão. Tente novamente.",
        });
        event.preventDefault();
      } else if (isReactDevelopmentError) {
        console.error("Erro crítico do React (não interceptado):", error);
        return;
      } else {
        console.error("Erro interceptado em produção:", error);
        addNotification({
          type: "error",
          content:
            "Algo deu errado. Atualize a página se o problema persistir.",
        });
        event.preventDefault();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      const isAxiosError = reason?.response?.status || reason?.config?.url;
      const isNetworkError =
        reason?.code === "ERR_NETWORK" ||
        reason?.message?.includes("Network Error");

      if (process.env.NODE_ENV === "development") {
        console.error(
          "Promise rejeitada não tratada (desenvolvimento):",
          reason
        );

        if (isAxiosError || isNetworkError) {
          const status = reason.response?.status;
          let message = "Erro de API em desenvolvimento.";

          if (status) {
            switch (status) {
              case 401:
                message = "Erro de autenticação (401).";
                break;
              case 404:
                message = "Recurso não encontrado (404).";
                break;
              case 500:
                message = "Erro do servidor (500).";
                break;
              default:
                message = `Erro de API (${status}).`;
            }
          }

          addNotification({
            type: "error",
            content: message,
          });
          event.preventDefault();
        }
        return;
      }

      console.error("Promise rejeitada não tratada (produção):", reason);

      if (isAxiosError) {
        const status = reason.response?.status;
        let message = "Erro na operação. Tente novamente.";

        switch (status) {
          case 401:
            message = "Sessão expirada. Faça login novamente.";
            break;
          case 403:
            message = "Você não tem permissão para esta ação.";
            break;
          case 404:
            message = "Recurso não encontrado.";
            break;
          case 500:
            message = "Erro do servidor. Tente novamente mais tarde.";
            break;
        }

        addNotification({
          type: "error",
          content: message,
        });
        event.preventDefault();
      } else if (isNetworkError) {
        addNotification({
          type: "error",
          content: "Problema de conexão. Verifique sua internet.",
        });
        event.preventDefault();
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, [addNotification]);
};
